import { NoInfer } from 'react-redux';

import {
  ActionReducerMapBuilder,
  Dispatch,
  Draft,
  PayloadAction,
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit';

import { RootState } from 'store/rootReducer';
import { FulfilledActionType, errorOr, fulfilled, pending, rejected } from 'store/storeHelpers';

import {
  Aktivitet,
  AktivitetState,
  DeleteAktivitetRequest,
  HTTP_STATUS,
  ModelError,
  SkyddsklassningSbmPost,
  Skyddsklassningsaktivitet
} from 'models';

import BebyggelseApi from 'api/bebyggelseApi';
import { aktivitetPreliminarSlice } from 'api/endpoints/aktivitetPreliminarSlice';
import {
  isEnkelinventering,
  isInventering,
  isKulturvardeForstort,
  isMigreringsaktivitet,
  isSkyddsklassningsaktivitet
} from 'utilities/typeGuards';

const requestCondition = (getState: () => unknown) => {
  const { aktivitet } = getState() as RootState;
  if (aktivitet.requestStatus === 'pending') return false;
};

export const getAktivitet = createAsyncThunk<Aktivitet & { eTag?: string }, string, { rejectValue: ModelError }>(
  'get/aktivitet',
  async (id: string, { rejectWithValue }) => {
    const response = await BebyggelseApi.aktivitet.get(id);
    return errorOr(response, rejectWithValue);
  },
  {
    condition: (_id: string, { getState }) => requestCondition(getState)
  }
);

export const postSkyddsklassningSbm = createAsyncThunk<
  Skyddsklassningsaktivitet,
  { post: SkyddsklassningSbmPost; bebyggelseobjektId?: string },
  { rejectValue: ModelError }
>('post/skyddsklassningSbm', async ({ post, bebyggelseobjektId }, { rejectWithValue }) => {
  const response = await BebyggelseApi.aktivitet.postSkyddsklassningSbm(post, bebyggelseobjektId);
  return errorOr(response, rejectWithValue);
});

export const patchSkyddsklassningPublicera = createAsyncThunk<Aktivitet, string, { rejectValue: ModelError }>(
  'patch/skyddsklassningsAktivitet/publicera',
  async (id: string, { rejectWithValue }) => {
    const response = await BebyggelseApi.aktivitet.patchSkyddsklassningPublicera(id);
    return errorOr(
      response,
      rejectWithValue,
      [HTTP_STATUS.UNPROCESSABLE_ENTITY],
      false,
      response.status === HTTP_STATUS.CREATED
    );
  }
);

export const deleteSkyddsklassning = createAsyncThunk<undefined, DeleteAktivitetRequest, { rejectValue: ModelError }>(
  'delete/skyddsklassning',
  async (params, { rejectWithValue }) => {
    const response = await BebyggelseApi.aktivitet.removeSkyddsklassning(params);
    return errorOr(response, rejectWithValue);
  },
  {
    condition: (_aktivitet, { getState }) => requestCondition(getState)
  }
);

export const clearInventering = () => {
  return (dispatch: Dispatch) => {
    dispatch(clearInventeringState());
    dispatch(aktivitetPreliminarSlice.util.resetApiState());
  };
};

export const clearSkyddsklassning = () => {
  return (dispatch: Dispatch) => {
    dispatch(clearInventeringState());
  };
};

const initialState: AktivitetState = {
  requestStatus: 'idle',
  requestType: undefined,
  currentRequestId: undefined,
  data: {},
  error: undefined,
  registrationActive: false
};

const aktivitetSlice = createSlice({
  name: 'aktivitet',
  initialState,
  reducers: {
    clearInventeringState: (state: Draft<AktivitetState>) => {
      if (state.data?.inventering) {
        state.data.inventering = undefined;
      }
    },
    clearSkyddsklassningState: (state: Draft<AktivitetState>) => {
      if (state.data?.skyddsklassning) {
        state.data.skyddsklassning = undefined;
      }
    },
    clearAktivitetState: (state: Draft<AktivitetState>) => {
      state.data = undefined;
    },
    setRegistrationActive: (state: Draft<AktivitetState>, payloadAction: PayloadAction<boolean>) => {
      state.registrationActive = payloadAction.payload;
    }
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<AktivitetState>>) => {
    /**
     * GET Aktivitet
     */
    builder.addCase(getAktivitet.pending, (state: Draft<AktivitetState>, action): void => pending(state, action));
    builder.addCase(getAktivitet.fulfilled, (state: Draft<AktivitetState>, action): void => {
      const { eTag, ...aktivitet } = action.payload;
      const inventering = isInventering(aktivitet) ? aktivitet : state.data?.inventering;
      const skyddsklassning = isSkyddsklassningsaktivitet(aktivitet) ? aktivitet : state.data?.skyddsklassning;
      const migrering = isMigreringsaktivitet(aktivitet) ? aktivitet : state.data?.migrering;
      const enkelinventering = isEnkelinventering(aktivitet) ? aktivitet : state.data?.enkelinventering;
      const kulturvardeForstort = isKulturvardeForstort(aktivitet) ? aktivitet : state.data?.kulturvardeForstort;

      const newAction: FulfilledActionType<AktivitetState['data']> = {
        payload: { inventering, skyddsklassning, migrering, enkelinventering, kulturvardeForstort, eTag },
        meta: action.meta,
        type: action.type
      };
      fulfilled(state, newAction);
    });
    builder.addCase(getAktivitet.rejected, (state: Draft<AktivitetState>, action): void => rejected(state, action));

    /**
     * POST Skyddsklassningsaktivitet
     */
    builder.addCase(postSkyddsklassningSbm.pending, (state: Draft<AktivitetState>, action): void =>
      pending(state, action)
    );
    builder.addCase(postSkyddsklassningSbm.fulfilled, (state: Draft<AktivitetState>, action): void =>
      fulfilled(state, { ...action, payload: { ...state.data, skyddsklassning: action.payload } })
    );
    builder.addCase(postSkyddsklassningSbm.rejected, (state: Draft<AktivitetState>, action): void =>
      rejected(state, action)
    );

    /**
     * PATCH Publicera Skyddsklassningsaktivitet
     */
    builder.addCase(patchSkyddsklassningPublicera.pending, (state: Draft<AktivitetState>, action): void =>
      pending(state, action)
    );
    builder.addCase(patchSkyddsklassningPublicera.fulfilled, (state: Draft<AktivitetState>, action): void => {
      // 201 PUBLICERAD utan granskning
      if (action.payload === undefined) {
        fulfilled(state, { ...action, payload: { ...state.data } });
        // 202 Skickad till Granska
      } else {
        const skyddsklassning = isSkyddsklassningsaktivitet(action.payload)
          ? action.payload
          : state.data?.skyddsklassning;
        fulfilled(state, { ...action, payload: { ...state.data, skyddsklassning } });
      }
    });
    builder.addCase(patchSkyddsklassningPublicera.rejected, (state: Draft<AktivitetState>, action): void =>
      rejected(state, action)
    );

    /**
     * DELETE Skyddsklassning
     */
    builder.addCase(deleteSkyddsklassning.pending, (state: Draft<AktivitetState>, action): void =>
      pending(state, action)
    );
    builder.addCase(deleteSkyddsklassning.fulfilled, (state: Draft<AktivitetState>, action): void => {
      const newAction: FulfilledActionType<AktivitetState['data']> = {
        payload: {
          inventering: undefined,
          skyddsklassning: undefined,
          migrering: undefined,
          enkelinventering: undefined,
          kulturvardeForstort: undefined,
          eTag: undefined
        },
        meta: action.meta,
        type: action.type
      };
      fulfilled(state, newAction);
    });
    builder.addCase(deleteSkyddsklassning.rejected, (state: Draft<AktivitetState>, action): void =>
      rejected(state, action)
    );
  }
});

export const { setRegistrationActive, clearInventeringState, clearAktivitetState } = aktivitetSlice.actions;

export default aktivitetSlice.reducer;
